import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle, Award, Wrench, Clock, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import ProductCard from '../components/ProductCard';
import { getMachines, getAccessories, getServices } from '../services/productApi';
import { features, services as mockServices } from '../data/mock';
import AvailabilityBadge from '../components/AvailabilityBadge';

// Map URL paths to category filters
const pathToCategoryMap = {
  '/bagger': { name: 'Bagger', nameKey: 'nav.bagger', filter: 'Mini-Bagger', urlPrefix: '/bagger' },
  '/radlader': { name: 'Radlader', nameKey: 'nav.radlader', filter: 'Radlader', urlPrefix: '/radlader' },
  '/gabelstapler': { name: 'Gabelstapler', nameKey: 'nav.gabelstapler', filter: 'Gabelstapler', urlPrefix: '/gabelstapler' },
  '/ruettelplatten': { name: 'Rüttelplatten', nameKey: 'nav.ruettelplatten', filter: 'Rüttelplatte', urlPrefix: '/ruettelplatten' },
  '/stampfer': { name: 'Stampfer', nameKey: 'nav.stampfer', filter: 'Stampfer', urlPrefix: '/stampfer' },
};

const Machines = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [machines, setMachines] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [services, setServices] = useState(mockServices);
  const [loading, setLoading] = useState(true);

  // Get active category from URL path
  const currentPath = location.pathname;
  const categoryConfig = pathToCategoryMap[currentPath] || pathToCategoryMap['/bagger'];
  const activeCategory = categoryConfig.name;
  const isBaggerPage = currentPath === '/bagger';

  // Load data from API
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [machinesData, accessoriesData, servicesData] = await Promise.all([
          getMachines(),
          getAccessories(),
          getServices()
        ]);
        
        setMachines(machinesData || []);
        setAccessories(accessoriesData || []);
        
        if (servicesData?.length > 0) {
          setServices(servicesData);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setMachines([]);
        setAccessories([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Gefilterte Maschinen basierend auf aktiver Kategorie
  const filteredMachines = React.useMemo(() => {
    if (!categoryConfig) return [];
    return machines.filter(m => m.category === categoryConfig.filter);
  }, [machines, categoryConfig]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" key={activeCategory}>
      {/* 1. Produkt-Kacheln */}
      <section className="pt-4 pb-8 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
            {filteredMachines.map((machine) => (
              <ProductCard
                key={machine.id}
                product={machine}
                linkPrefix={categoryConfig.urlPrefix}
                showButtons={true}
              />
            ))}
          </div>

          {filteredMachines.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">{t('products.notAvailable')}</p>
            </div>
          )}
        </div>
      </section>

      {/* 2. Zubehör-Kacheln (NUR auf Bagger-Seite) */}
      {isBaggerPage && accessories.length > 0 && (
        <section className="py-4 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">{t('nav.zubehoer')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {accessories.map((item) => (
                <ProductCard
                  key={item.id || item.slug}
                  product={{
                    ...item,
                    pricing: {
                      buy: `${item.price?.toLocaleString('de-DE') || '0'} €`,
                      numericPrice: item.price || 0
                    }
                  }}
                  linkPrefix="/zubehoer"
                  showButtons={true}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 3. Info-Bereich: 4 Vorteile + 3 Leistungen + CTA */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="bg-gray-50 rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6">
            
            {/* 4 Vorteile - Qualität, Service, Schnelle Verfügbarkeit, Persönliche Beratung */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {features.map((feature, index) => {
                const IconComponent = {
                  Award,
                  Wrench,
                  Clock,
                  HeadphonesIcon: Phone
                }[feature.icon];
                
                return (
                  <Card key={index} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                    <CardContent className="pt-6 pb-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                          <IconComponent className="text-red-600" size={24} />
                        </div>
                        <h3 className="font-semibold text-sm sm:text-lg mb-2">{feature.title}</h3>
                        <p className="text-gray-600 text-xs sm:text-sm">{feature.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* 3 Leistungen - Verkauf, Beratung, Service */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {services.map((service) => (
                <Card key={service.id} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                        {service.id === 1 && <Award className="text-red-600" size={24} />}
                        {service.id === 2 && <Phone className="text-red-600" size={24} />}
                        {service.id === 3 && <Wrench className="text-red-600" size={24} />}
                      </div>
                      <h3 className="font-semibold text-sm sm:text-lg mb-2">{service.title}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm mb-3">{service.description}</p>
                      <ul className="space-y-1 text-left w-full">
                        {service.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-start text-xs sm:text-sm">
                            <CheckCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={14} />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* CTA - Bereit für Ihr Projekt */}
            <Card className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
              <CardContent className="pt-8 pb-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                    <ArrowRight className="text-red-600" size={24} />
                  </div>
                  <h3 className="font-semibold text-lg sm:text-xl mb-2">{t('cta.readyForProject')}</h3>
                  <p className="text-gray-600 text-sm sm:text-base mb-4 max-w-xl">
                    {t('cta.contactUs')}
                  </p>
                  <Link to="/kontakt">
                    <Button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center">
                      {t('cta.getInTouch')}
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* 4. Zwei Videos am Ende */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
            {/* Video 2 */}
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/videos/video2-poster.jpg"
                className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover"
                webkit-playsinline="true"
              >
                <source src="/videos/video2.mp4" type="video/mp4" />
              </video>
            </div>

            {/* Video 3 */}
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/videos/video3-poster.jpg"
                className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover"
                webkit-playsinline="true"
              >
                <source src="/videos/video3.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Machines;
